import { AsyncPipe, CommonModule, NgClass } from '@angular/common';
import { Component, effect, HostListener, inject, OnInit, Signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { RestrictedElementDirective } from '@auth';
import { FilterClientsByNamePipe } from '@clients';
import { AppService, CURRENT_CLIENT_ID_KEY, FeatureFlagPipe, SortPipe } from '@core';
import { DashboardsSelectorComponent } from '@dashboards';
import { environment } from '@env';
import { UcMonitoringStateStore } from '@locations';
import { ChildClient, ClientStatus } from '@models';
import { TippyDirective } from '@ngneat/helipopper';
import { Store } from '@ngrx/store';
import { AuthActions, AuthSelectors, ClientManagementActions, ClientManagementSelectors, IncidentsSelectors } from '@ngrx-store';
import { LayoutService, SearchInputComponent } from '@ui';
import { filter, Observable } from 'rxjs';

import { InstanceBadgeComponent } from '../instance-badge/instance-badge.component';

@Component({
  imports: [MatSidenavModule, MatListModule, MatIcon, MatTooltipModule, InstanceBadgeComponent, SearchInputComponent, MatLabel, NgClass, RouterModule, AsyncPipe, RestrictedElementDirective, DashboardsSelectorComponent, TippyDirective, CommonModule, FilterClientsByNamePipe, SortPipe, MatPrefix, FeatureFlagPipe],
  selector: 'avs-fe-main-menu',
  styleUrl: './main-menu.component.scss',
  templateUrl: './main-menu.component.html',
})
export class MainMenuComponent implements OnInit {
  protected clientsList = [] as ChildClient[];
  protected clientSearchValue = '';
  protected dashboardsSelector = viewChild(DashboardsSelectorComponent);
  protected readonly environment = environment;
  protected resizeTimeout!: ReturnType<typeof setTimeout>;
  protected showMenuSizeToggle = false;
  protected isSuperAdmin$: Observable<boolean> | undefined;
  protected incidentsCount$: Observable<number | undefined>;
  protected selectedClient: Signal<ChildClient | undefined>;
  protected layout = inject(LayoutService);
  protected readonly ucMonitoring = inject(UcMonitoringStateStore);

  private readonly MENU_SHRINK_WIDTH = 700;
  private readonly RESIZE_DEBOUNCE_TIME = 1000;

  private appService = inject(AppService);
  private router = inject(Router);
  private store = inject(Store);

  constructor() {
    this.incidentsCount$ = this.store.select(IncidentsSelectors.selectIncidentsCount);
    this.isSuperAdmin$ = this.store.select(AuthSelectors.isSuperAdmin);
    this.selectedClient = toSignal(this.store.select(ClientManagementSelectors.getCurrentClient));

    effect(() => {
      const client = this.selectedClient();

      if (client) {
        untracked(() => {
          this.dashboardsSelector()?.refresh();
          this.ucMonitoring.reload();
        });
      }
    });

    this.isSuperAdmin$
      .pipe(
        filter(isSuperAdmin => isSuperAdmin),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.store.dispatch(ClientManagementActions.loadChildClients()));

    this.store.select(ClientManagementSelectors.selectChildClients)
      .pipe(takeUntilDestroyed())
      .subscribe(clientList => {
        this.clientsList = clientList || [];
        if (clientList && clientList.length) {
          this.clientsList = this.clientsList.filter(item => item.status !== ClientStatus.ARCHIVED);
          const currentClientId = this.appService.currentClient;
          const currentClient = clientList?.find(client => client.id === currentClientId);

          if (!currentClient) {
            this.store.dispatch(ClientManagementActions.clientIdChanged({ clientId: clientList[0].id }));
          }
        }
      });
  }

  ngOnInit() {
    this.autosizeMenu();
  }

  @HostListener('window:resize')
  protected onResize() {
    this.autosizeMenu();
  }

  protected toggleMenu() {
    this.layout.menuShrunk.update(menuShrunk => !menuShrunk);
  }

  protected autosizeMenu() {
    clearTimeout(this.resizeTimeout);

    const { menuShrunk, showMenuSizeToggle } = this.determineMenuState();

    this.showMenuSizeToggle = showMenuSizeToggle;

    if (!this.showMenuSizeToggle) {
      this.resizeTimeout = setTimeout(() => {
        this.layout.menuShrunk.update(() => menuShrunk);
      }, this.RESIZE_DEBOUNCE_TIME);
    }
  }

  protected logout() {
    this.store.dispatch(AuthActions.logout());
  }

  protected async clientChange(client: ChildClient) {
    this.store.dispatch(ClientManagementActions.clientIdChanged({ clientId: client.id }));
    this.resetSearch();

    await this.router.navigate(['dashboard'], {
      queryParams: { [CURRENT_CLIENT_ID_KEY]: client.id },
    });
  }

  protected resetSearch() {
    this.clientSearchValue = '';
  }

  protected clientSearch(value: string) {
    this.clientSearchValue = value;
  }

  private determineMenuState() {
    const isMenuShrunk = window.innerWidth < this.MENU_SHRINK_WIDTH;

    return {
      menuShrunk: isMenuShrunk,
      showMenuSizeToggle: !isMenuShrunk,
    };
  }
}
